import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type AudienceAgeAndGenderView = {
  /** `Largest segment` and `Most negative segment`. */
  kpis: KPIMetric[];
  /** One row per age band (youngest first), one key per gender — negative mentions. */
  rows: ChartRow[];
  xKey: string;
  /** The genders present; the first is drawn left of zero. */
  series: ChartSeries[];
  /** Whether any negative mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
};

export type AudienceAgeAndGenderCardProps = WidgetBlockProps & {
  view?: AudienceAgeAndGenderView;
  /** Opens an age band's negative mentions (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:19575` — who is complaining, by age band and gender.
 *
 * The pyramid plots **negative** mentions, not all of them, and that is the
 * card's whole argument: the aggregate age and gender split of an audience
 * barely moves in a fortnight, so a pyramid of it would be the same shape every
 * time anyone looked. The complaint skew inside it does move.
 *
 * That skew had a third KPI — the male share of complaints less the male share
 * of the base, in points — removed on the user's instruction (2026-09-23). The
 * two segment figures stay; the pyramid is where the skew is read now.
 *
 * The bands keep their own order (youngest first) rather than a ranking by
 * volume, which would put `25-34` at the top and make the pyramid meaningless.
 */
export function AudienceAgeAndGenderCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: AudienceAgeAndGenderCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No negative mentions in this period"
      {...cardState(status, hasData, onRetry)}
      title="Audience age and gender"
      helpText="Who is complaining, by age band and gender, against the shape of the audience they came from."
      helpSupportingText="The bars are negative mentions only. The aggregate age and gender split of an audience barely moves in a fortnight; the complaint skew inside it does, which is what the pyramid is drawn on negatives to show."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            diverging
            /* Age bands are four characters; the 128 default would be gutter. */
            categoryAxisWidth={52}
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
